<template>
  <v-form ref="formRef" class="pump-form method-form" @submit.prevent="">
    <!-- Pumpendaten — base data plus the inputs the selected calculation method needs. These used
         to sit in a separate "Berechnungsmethode" section, which split one short list of pump
         properties across two headings for no gain. -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-pump</v-icon>
        <span>Pumpendaten</span>
      </div>

      <!-- Amount -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        label="Anzahl"
        :error-messages="validationErrors?.amount"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <!-- != null catches 0 as a valid reference value (falsy check would hide the button) -->
          <v-btn
            v-if="!reference && referenceData?.amount != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Power (hidden for the DN-table mode, which uses the nominal width instead) -->
      <v-text-field
        v-if="sharedData.calculation_method !== 'wet_dn'"
        v-model.number="form.powerRating"
        type="number"
        label="Nennleistung"
        suffix="kW"
        :error-messages="validationErrors?.power_rating"
        :hint="hint.power_rating"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.power_rating" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.power_rating }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.power_rating != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('power_rating')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Maintenance condition (feeds the Nutzen-fallback efficiency).
           Reference only: a planned pump is new, so wear is not something the user can observe or
           enter. The backend evaluates the planned variant as-new regardless of what is stored. -->
      <v-autocomplete
        v-if="props.reference"
        v-model="form.condition"
        :items="conditions"
        item-title="text"
        item-value="value"
        label="Wartungszustand"
        :error-messages="validationErrors?.condition"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.condition" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.condition }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Regulation mode -->
      <template v-if="sharedData.calculation_method === 'regulation'">
        <v-autocomplete
          v-model="form.regulation"
          :items="regulations"
          item-title="text"
          item-value="value"
          label="Regelung"
          :error-messages="validationErrors?.regulation"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.regulation" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">mdi-information</v-icon>
              </template>
              <span>{{ hint.regulation }}</span>
            </v-tooltip>
          </template>
        </v-autocomplete>
      </template>

      <!-- Efficiency mode -->
      <template v-if="sharedData.calculation_method === 'efficiency_class'">
        <v-text-field
          v-model.number="form.yearBuilt"
          type="number"
          label="Baujahr"
          :error-messages="validationErrors?.year_built"
          :hint="hint.year_built"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.year_built" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">mdi-information</v-icon>
              </template>
              <span>{{ hint.year_built }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.year_built != null"
              icon
              title="Referenzwert übernehmen"
              @click="copyFromReference('year_built')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-checkbox
          v-model="form.vsdFu"
          label="VSD / FU"
          :error-messages="validationErrors?.vsd_fu"
          :hint="hint.vsd_fu"
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.vsd_fu" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">mdi-information</v-icon>
              </template>
              <span>{{ hint.vsd_fu }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.vsd_fu != null"
              icon
              title="Referenzwert übernehmen"
              @click="copyFromReference('vsd_fu')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-checkbox>
      </template>

      <!--
        Mean distribution load β (WP-P5 / decision D-3).
        Shown only where it changes something: the two dry-rotor modes whose control factor comes
        from DIN V 18599-5 Tabelle 28, and only under the new Rechenmethode — the old one uses a
        flat house value that does not vary with load. Optional throughout; left empty it falls
        back to the certified module's operating point (β = 0,457), so the result is unchanged.
      -->
      <v-text-field
        v-if="showMeanLoad"
        v-model.number="form.meanLoad"
        type="number"
        step="0.05"
        label="Mittlere Auslastung β"
        placeholder="0,457 (Standardwert)"
        persistent-placeholder
        :error-messages="validationErrors?.mean_load"
        :hint="hint.mean_load"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.mean_load" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.mean_load }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.mean_load != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('mean_load')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Wet-rotor nameplate mode (Leistungsschild P1) deliberately renders nothing: it needs no
           input beyond Nennleistung above, which is the P1 value itself. What the plate reading
           means is documented on the Nennleistung tooltip (model help_text), so there is nothing
           left to show here. -->

      <!-- Wet-rotor DN-table mode -->
      <template v-if="sharedData.calculation_method === 'wet_dn'">
        <v-autocomplete
          v-model="form.nominalWidth"
          :items="nominalWidths"
          item-title="width"
          item-value="width"
          label="Nennweite DN"
          :error-messages="validationErrors?.nominal_width"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-btn
              v-if="!reference && referenceData?.nominal_width != null"
              icon
              title="Referenzwert übernehmen"
              @click="copyFromReference('nominalWidth')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-autocomplete>

        <v-autocomplete
          v-model="form.wetRegulation"
          :items="wetRegulations"
          item-title="text"
          item-value="value"
          label="Regelung (Nassläufer)"
          :error-messages="validationErrors?.wet_regulation"
          validate-on-blur
          class="mb-3"
        />
      </template>
    </div>

    <!-- Betriebsstunden -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betriebsstunden</span>
      </div>

      <!-- Norm uptime toggle -->
      <v-checkbox
        v-model="form.checkNormUptime"
        label="Norm-Betriebsstunden"
        :error-messages="validationErrors?.check_norm_uptime"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.check_norm_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.check_norm_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.check_norm_uptime != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('check_norm_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!--
        Norm uptime select.
        NOTE: normUptimeObject is intentionally NOT in the fields map.
        It is a local-only UI binding used to let the user pick a norm uptime entry.
        On selection, we extract the id (-> form.normUptimeId for the backend)
        and the hours value (-> form.uptime for display/calculation).
        Only normUptimeId travels to the backend via the fields map.
      -->
      <v-autocomplete
        v-if="form.checkNormUptime"
        v-model="normUptimeObject"
        :items="uptimes"
        item-title="name"
        item-value="id"
        return-object
        label="Norm-Betriebsstunden"
        :error-messages="validationErrors?.norm_uptime"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.norm_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.norm_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.norm_uptime != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('norm_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Manual uptime (disabled when norm uptime is active) -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        label="Betriebsstunden"
        suffix="h/a"
        :disabled="form.checkNormUptime"
        :error-messages="validationErrors?.uptime"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!--
        Integrated pump management — DIN V 18599-5 §6.3.2.2, Gl. (56).
        Sits in the Betriebsstunden section because it is a RUNTIME factor: it shortens the hours
        the pump actually runs, which is why it applies to every calculation method rather than
        only the derived ones. Defaults to the neutral option, so nothing moves until it is set.
      -->
      <v-autocomplete
        v-model="form.pumpManagement"
        :items="pumpManagements"
        item-title="text"
        item-value="value"
        label="Pumpenmanagement"
        :error-messages="validationErrors?.pump_management"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.pump_management" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.pump_management }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.pump_management != null"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('pump_management')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import api from '@/plugins/axios'
import { useBaseForm } from '@/composables/useBaseForm'
import { djangoChoicesToVuetify } from '@/utils'

// ============================================================
// Props & Emits
// ============================================================

const props = defineProps({
  modelValue:       Object,
  reference:        Boolean,
  planned:          Boolean,
  otherData:        Object,
  sharedData:       Object,
  validationErrors: Object,
  methodValue:      String,
})

const emit = defineEmits(['update:modelValue'])

// ============================================================
// Field mapping (frontend key -> backend key)
//
// Each entry here becomes a form field (form.frontendKey) and is
// included in the backend payload as its mapped backend key.
//
// normUptimeObject is intentionally excluded — it is a local UI
// binding only. The backend receives the selected id via normUptimeId.
// ============================================================

const fields = {
  amount:          'amount',
  powerRating:     'power_rating',
  regulation:      'regulation',
  yearBuilt:       'year_built',
  vsdFu:           'vsd_fu',
  condition:       'condition',
  nominalWidth:    'nominal_width',   // wet_dn: DN width value (FK to_field='width')
  wetRegulation:   'wet_regulation',  // wet_dn
  uptime:          'uptime',
  checkNormUptime: 'check_norm_uptime',
  normUptimeId:    'norm_uptime',     // sends only the id, not the full object
  pumpManagement:  'pump_management', // f_d,PM — DIN V 18599-5 §6.3.2.2 (runtime factor)
  meanLoad:        'mean_load',       // β_h,d  — DIN V 18599-5 §6.3.2 (Tabelle 28 load point)
}

// ============================================================
// Base form composable
// ============================================================

const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value:       computed(() => props.modelValue),
  fields,
  reference:   computed(() => props.reference),
  planned:     computed(() => props.planned),
  shared:      computed(() => false),           // this is always a normal (non-shared) child form
  otherData:   computed(() => props.otherData),
  sharedData:  computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit:        (e, p) => emit(e, p),
})

// ============================================================
// Local state
// ============================================================

const uptimes       = ref([])
const regulations   = ref([])
const wetRegulations = ref([])
const nominalWidths = ref([])
const conditions    = ref([])
const pumpManagements = ref([])

// β only feeds the two dry-rotor modes that read DIN V 18599-5 Tabelle 28, and only under the new
// Rechenmethode (calc_version 2 = PumpCommon.CALC_METHOD_18599). Anywhere else it is inert, so
// showing it would imply an effect the calculation does not have.
const BETA_MODES = ['regulation', 'efficiency_class']
const showMeanLoad = computed(() =>
  BETA_MODES.includes(props.sharedData.calculation_method) &&
  props.sharedData.calc_version === 2,
)

// normUptimeObject is local-only — it holds the full object from the autocomplete
// so we can extract both the id (for the backend) and the hours value (for uptime).
// It is NOT in the fields map and does NOT travel to the backend directly.
const normUptimeObject = ref(null)

// ============================================================
// Watchers
// ============================================================

// When the user selects a norm uptime entry, extract:
//   - its id into form.normUptimeId  (this is what gets sent to the backend)
//   - its hours value into form.uptime (so the uptime field reflects the norm value)
// Guard: only apply when checkNormUptime is active, to avoid overwriting
// a manually entered uptime if the user deselects norm uptime mode.
watch(normUptimeObject, obj => {
  if (form.checkNormUptime && obj) {
    form.normUptimeId = obj.id
    form.uptime       = obj.norm_uptime
  }
})

// Re-fetch regulations when reference/planned mode changes.
// The available regulation options may differ between reference and planned mode,
// so we can't rely on the onMounted fetch alone.
watch(
  () => [props.planned, props.reference],
  () => fetchRegulations(),
)

// ============================================================
// API calls
// ============================================================

async function fetchNormUptimes() {
  const { data } = await api.get('/methods/pump1/norm_uptimes/')
  uptimes.value = data
}

// Fetches regulation choices scoped to the current reference/planned mode.
// Called on mount and again whenever reference or planned props change.
async function fetchRegulations() {
  const { data } = await api.get('/methods/pump1/regulation/', {
    params: {
      planned:   props.planned   || null,
      reference: props.reference || null,
    },
  })
  regulations.value = djangoChoicesToVuetify(data)
}

async function fetchWetRegulations() {
  const { data } = await api.get('/methods/pump1/wet-regulation/')
  wetRegulations.value = djangoChoicesToVuetify(data)
}

async function fetchConditions() {
  const { data } = await api.get('/methods/pump1/conditions/')
  conditions.value = djangoChoicesToVuetify(data)
}

async function fetchNominalWidths() {
  const { data } = await api.get('/methods/pump1/nominal-width/')
  nominalWidths.value = data
}

async function fetchPumpManagements() {
  const { data } = await api.get('/methods/pump1/pump-management/')
  pumpManagements.value = djangoChoicesToVuetify(data)
}

// ============================================================
// Expose (for parent form validation)
// ============================================================

const formRef     = ref(null)
const validateForm = () => formRef.value?.validate?.()

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

// ============================================================
// Lifecycle
// ============================================================

onMounted(async () => {
  await fetchNormUptimes()
  await fetchRegulations()
  await fetchWetRegulations()
  await fetchConditions()
  await fetchNominalWidths()
  await fetchPumpManagements()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.pump-form {
  width: 100%;
}

/* Field styling */
.pump-form :deep(.v-field) {
  border-radius: 18px;
}

.pump-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkboxes */
.pump-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested/dependent fields */
.pump-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons */
.pump-form :deep(.v-input__append),
.pump-form :deep(.v-field__append-inner) {
  align-items: center;
}

.pump-form :deep(.v-input__append .v-icon),
.pump-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.pump-form :deep(.v-input__append .v-btn),
.pump-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.pump-form :deep(.v-input__append .v-btn:hover),
.pump-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled fields */
.pump-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .pump-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>